import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import Sidebar from '../Sidebar/Sidebar';
import { DocketDetailsProps } from '../../common/types';
import DocketDisplayItemIcon from '../DocketItem/DocketItemEntities/DocketDisplayItemIcon';
import DocketDetailsStyles from './DocketDetails.styles';
import {
  DEFAULT_TABLE_ABBREVIATION,
  DocketItemStatus,
  DocketPayloadInput,
  DocketStatus,
  NotifierDisplayStyle,
  NotifierDocketItems,
} from '@oolio-group/domain';
import DocketItemStyles from '../DocketItem/DocketItem.styles';
import AllDayViewStyles from '../AllDayView/AllDayView.styles';
import { useColors } from '../../hooks/useColors';
import { useDockets } from '../../hooks/useDockets';
import { useSession } from '../../hooks/useSession';
import {
  getOrderTypeName,
  minifyOrderNumber,
} from '../../utils/docketHelper/transformDockets';
import DocketItemBase from '../DocketItem/DocketItemEntities/DocketItemBase';
import { useOrderTypes } from '../../hooks/useOrderTypes';

export interface Props {
  onClose?: () => void;
  docketDetails: DocketDetailsProps;
}

const DocketDetails = ({ onClose = () => undefined, docketDetails }: Props) => {
  const { colors } = useColors();
  const { translate } = useTranslation();
  const styles = DocketDetailsStyles();
  const itemStyles = DocketItemStyles();
  const btnStyles = AllDayViewStyles();

  const {
    bumpDocketWithStatus,
    recallDocket,
    bumpDocketItem,
    recallDocketItem,
    serveDocketItem,
    reBumpDocketItem,
    printKitchenDocket,
  } = useDockets();
  const { session } = useSession();

  const printerProfiles = session?.allPrinterProfiles;
  const availableProfiles = useMemo(() => {
    if (!printerProfiles || printerProfiles.length === 0) {
      return new Map<string, string>();
    }
    return new Map(printerProfiles.map(profile => [profile.id, profile.name]));
  }, [printerProfiles]);

  const [docketItems, setLocalDocketItems] = useState(docketDetails?.items);
  const [notifierItems, setLocalNotifierItems] = useState(
    docketDetails?.notifierItems?.flatMap(item => item.items),
  );
  const [initialDisplayStyle] = useState(docketDetails.displayStyle);
  const { orderTypes } = useOrderTypes();
  const orderTypeName = getOrderTypeName(docketDetails.orderTypeId, orderTypes);
  const seatManagementEnabled = useMemo(() => {
    return (
      (session?.enableSeatManagement && orderTypeName === 'Dine In') || false
    );
  }, [session?.enableSeatManagement, orderTypeName]);

  useEffect(() => {
    setLocalDocketItems(docketDetails?.items);
    setLocalNotifierItems(
      docketDetails?.notifierItems?.flatMap(item => item.items),
    );
  }, [docketDetails]);

  const getTextStyle = useCallback(
    (itemStatus: DocketItemStatus, docketStatus?: DocketStatus) => {
      const status = docketStatus || docketDetails.status;
      const nameStyle = itemStyles.name;
      const textStyle: TextStyle[] = [nameStyle];
      if (status === DocketStatus.COMPLETED) {
        textStyle.push(itemStyles.servedItem);
      }
      switch (itemStatus) {
        case DocketItemStatus.TRANSFERRED:
          textStyle.push(itemStyles.transferredItem);
          break;
        case DocketItemStatus.VOIDED:
          textStyle.push(itemStyles.voidedItem);
          break;
        case DocketItemStatus.BUMPED:
          if (status == DocketStatus.CREATED) {
            textStyle.push(itemStyles.preppedItem); // Prepped
          }
          break;
        case DocketItemStatus.REBUMPED:
          if (status == DocketStatus.CREATED) {
            textStyle.push(itemStyles.completedItem); // Completed
          }
          break;
        case DocketItemStatus.SERVED:
          if (status == DocketStatus.CREATED) {
            textStyle.push(itemStyles.servedItem); // Served
          }
          break;
        default:
          break;
      }
      return textStyle;
    },
    [
      docketDetails.status,
      itemStyles.name,
      itemStyles.servedItem,
      itemStyles.transferredItem,
      itemStyles.voidedItem,
      itemStyles.preppedItem,
      itemStyles.completedItem,
    ],
  );

  const handleOnPressBumpAll = useCallback(async () => {
    if (docketDetails.status === DocketStatus.CREATED) {
      const itemStatus = session?.rebumpToggle
        ? DocketItemStatus.REBUMPED
        : DocketItemStatus.BUMPED;

      return bumpDocketWithStatus(
        docketDetails.docketId,
        itemStatus,
        docketDetails.notifierItems,
      );
    } else {
      return recallDocket(docketDetails.docketId);
    }
  }, [
    docketDetails,
    session?.rebumpToggle,
    bumpDocketWithStatus,
    recallDocket,
  ]);

  const handleOnPressPrint = useCallback(async () => {
    return printKitchenDocket(
      docketDetails.docketId,
      docketDetails.status as DocketStatus,
      docketDetails.notifierItems,
    );
  }, [docketDetails, printKitchenDocket]);

  const handleOnPressDocketItem = useCallback(
    async (
      docketItemIds: string[],
      itemStatus: DocketItemStatus,
      status: DocketStatus,
    ) => {
      if (
        itemStatus == DocketItemStatus.CREATED ||
        itemStatus == DocketItemStatus.RECALLED
      ) {
        await Promise.all(docketItemIds.map(async id => bumpDocketItem(id)));
      } else if (
        status == DocketStatus.CREATED &&
        itemStatus == DocketItemStatus.BUMPED
      ) {
        if (session?.rebumpToggle) {
          await Promise.all(
            docketItemIds.map(async id => reBumpDocketItem(id)),
          );
        } else {
          await Promise.all(docketItemIds.map(async id => serveDocketItem(id)));
        }
      } else if (
        status == DocketStatus.CREATED &&
        itemStatus == DocketItemStatus.REBUMPED
      ) {
        await Promise.all(docketItemIds.map(async id => serveDocketItem(id)));
      } else {
        await Promise.all(docketItemIds.map(async id => recallDocketItem(id)));
      }
    },
    [
      bumpDocketItem,
      recallDocketItem,
      serveDocketItem,
      reBumpDocketItem,
      session?.rebumpToggle,
    ],
  );

  const renderActions = useMemo(() => {
    return (
      <View style={btnStyles.action}>
        <TouchableOpacity
          style={[btnStyles.btn, { backgroundColor: colors.orange }]}
          onPress={async () => {
            await handleOnPressBumpAll();
            onClose();
          }}
        >
          <Text style={btnStyles.btnText}>
            {translate('kitchenDisplay.bumpAll')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[btnStyles.btn, { backgroundColor: colors.blue }]}
          onPress={async () => {
            await handleOnPressPrint();
            onClose();
          }}
        >
          <Text style={btnStyles.btnText}>
            {translate('kitchenDisplay.printAll')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }, [
    btnStyles,
    colors,
    handleOnPressBumpAll,
    handleOnPressPrint,
    onClose,
    translate,
  ]);

  const renderCourseName = (courseName: string) => {
    if (!courseName) return null;
    return <DocketItemBase name={courseName} />;
  };

  const renderSectionDivider = useCallback(() => {
    if (
      !docketDetails?.notifierItems ||
      docketDetails.notifierItems.length === 0
    )
      return null;

    let printerText = '';
    const printerProfiles = docketDetails.notifierItems || [];

    // Use the stored initial displayStyle instead of the changing one
    if (
      initialDisplayStyle === NotifierDisplayStyle.SHOW_SUMMARY &&
      printerProfiles.length > 0
    ) {
      if (printerProfiles.length === 1) {
        const profileName = availableProfiles.get(
          printerProfiles[0].printerProfileId,
        );

        if (profileName) {
          printerText = ` (${profileName})`;
        }
      } else {
        const firstProfileName = availableProfiles.get(
          printerProfiles[0].printerProfileId,
        );
        if (firstProfileName) {
          const othersCount = printerProfiles.length;
          printerText = ` (${othersCount})`;
        }
      }
    }

    return (
      <View style={styles.sectionDivider}>
        <Text style={styles.rowText}>
          {translate('kitchenDisplay.otherItems')}
          {printerText}
        </Text>
      </View>
    );
  }, [
    availableProfiles,
    docketDetails?.notifierItems,
    translate,
    styles,
    initialDisplayStyle,
  ]);

  const groupedItems = useMemo(() => {
    const groups: Record<string, any[]> = {};
    docketItems?.forEach(item => {
      const courseName = item?.course?.name || '';
      if (!groups[courseName]) {
        groups[courseName] = [];
      }
      groups[courseName].push(item);
    });
    return groups;
  }, [docketItems]);

  // Get an array of course names for rendering
  const courseNames = useMemo(() => {
    return Object.keys(groupedItems);
  }, [groupedItems]);

  const getSeatName = useCallback(
    (seatNumber?: number): string => {
      if (!seatManagementEnabled) {
        return '';
      }

      if (seatNumber === undefined) {
        return String(DEFAULT_TABLE_ABBREVIATION || '');
      }

      return `S${seatNumber}`;
    },
    [seatManagementEnabled],
  );

  // Extract notes rendering (reusable for both main and other items)
  const renderItemNotes = useCallback(
    (item: DocketPayloadInput) => {
      if (!item?.notes || item.notes.length === 0) return null;

      return (
        <View style={itemStyles.line}>
          <Text numberOfLines={3} style={styles.note}>
            {`▶ ${item.notes}`}
          </Text>
        </View>
      );
    },
    [itemStyles.line, styles.note],
  );

  const renderItemModifiers = useCallback(
    (item: DocketPayloadInput) => {
      if (!item?.modifiers || item.modifiers.length === 0) return null;

      return item.modifiers.map((modifier: any, subIndex: number) => (
        <Text
          key={`${modifier.name || 'modifier'}-${subIndex}`}
          style={styles.modifierName}
        >
          {`${modifier.quantity || 1} x ${modifier.name || ''}`}
        </Text>
      ));
    },
    [styles.modifierName],
  );

  const renderOtherItemRow = useCallback(
    (item: DocketPayloadInput) => {
      return (
        <View style={styles.itemRow}>
          <View style={styles.itemQuantity}>
            <Text
              style={[
                styles.qtyText,
                getTextStyle(item.itemStatus, item.status),
              ]}
            >
              {item.quantity || 1}
            </Text>
          </View>
          <View style={styles.itemDetails}>
            <Text
              style={[
                styles.itemName,
                getTextStyle(item.itemStatus, item.status),
              ]}
            >
              {item?.product?.name}
            </Text>
            {renderItemNotes(item)}
            {renderItemModifiers(item)}
          </View>
          <DocketDisplayItemIcon
            status={item.status}
            itemStatus={item.itemStatus}
            bumpItemLoading={false}
            isOtherItem={true}
          />
        </View>
      );
    },
    [styles, getTextStyle, renderItemModifiers, renderItemNotes],
  );

  // Extract full content rendering for other items
  const renderOtherItemFullContent = useCallback(
    (otherItem: NotifierDocketItems, index: number) => {
      return (
        <React.Fragment key={`otherItem-full-${index}`}>
          <Text style={itemStyles.secondaryTextStyle}>
            {availableProfiles.get(otherItem.printerProfileId) || ''}
          </Text>
          <FlatList
            data={otherItem.items}
            keyExtractor={(_, itemIndex) => `item-${index}-${itemIndex}`}
            renderItem={({ item }) => renderOtherItemRow(item)}
          />
        </React.Fragment>
      );
    },
    [availableProfiles, itemStyles.secondaryTextStyle, renderOtherItemRow],
  );

  // Extract summary rendering for other items
  const renderOtherItemSummary = useCallback(
    (otherItem: NotifierDocketItems, index: number) => {
      const totalQuantity =
        otherItem?.items?.reduce(
          (total: number, item: any) => total + (item.quantity ?? 0),
          0,
        ) || 0;

      return (
        <View key={`otherItem-summary-${index}`} style={styles.profileRow}>
          <Text style={styles.rowText}>
            {`${
              availableProfiles.get(otherItem.printerProfileId) || ''
            } (${totalQuantity})`}
          </Text>
        </View>
      );
    },
    [availableProfiles, styles.profileRow, styles.rowText],
  );

  const renderOtherItems = useCallback(() => {
    if (
      !notifierItems ||
      notifierItems.length === 0 ||
      !docketDetails.notifierItems
    ) {
      return null;
    }

    const isFullContent =
      initialDisplayStyle === NotifierDisplayStyle.SHOW_FULL_CONTENT;

    return docketDetails.notifierItems.map((otherItem, index) => {
      return isFullContent
        ? renderOtherItemFullContent(otherItem, index)
        : renderOtherItemSummary(otherItem, index);
    });
  }, [
    notifierItems,
    docketDetails.notifierItems,
    initialDisplayStyle,
    renderOtherItemFullContent,
    renderOtherItemSummary,
  ]);

  const renderMainItemRow = useCallback(
    (item: any) => {
      return (
        <TouchableOpacity
          style={styles.itemRow}
          onPress={() =>
            handleOnPressDocketItem(
              item?.consolidatedOrderItemIds || [item?.orderItemId],
              item.itemStatus,
              item.status,
            )
          }
        >
          <View style={styles.itemQuantity}>
            <Text style={[styles.qtyText, getTextStyle(item.itemStatus)]}>
              {item.quantity || 1}
            </Text>
          </View>
          <View style={styles.itemDetails}>
            <Text style={[styles.itemName, getTextStyle(item.itemStatus)]}>
              {item?.product?.name}
            </Text>
            {renderItemNotes(item)}
            {renderItemModifiers(item)}
          </View>
          <View style={styles.rightContainer}>
            {item.seatNumber && (
              <Text style={itemStyles.seat}>
                {getSeatName(item.seatNumber)}
              </Text>
            )}
            <DocketDisplayItemIcon
              status={item.status}
              itemStatus={item.itemStatus}
              bumpItemLoading={false}
            />
          </View>
        </TouchableOpacity>
      );
    },
    [
      styles,
      getTextStyle,
      handleOnPressDocketItem,
      renderItemNotes,
      renderItemModifiers,
      getSeatName,
      itemStyles.seat,
    ],
  );

  useEffect(() => {
    if (!docketDetails?.items || docketDetails.items.length === 0) {
      onClose();
    }
  }, [docketDetails?.items, onClose]);

  return (
    <Sidebar
      onClose={onClose}
      title={minifyOrderNumber(docketDetails.orderNumber) || ''}
      action={renderActions}
      height={60}
      placement="right"
    >
      <View style={styles.containerFullWidth}>
        {courseNames.map((courseName, courseIndex) => (
          <View key={`course-${courseIndex}`}>
            {courseName ? renderCourseName(courseName) : null}
            <FlatList
              data={groupedItems[courseName]}
              keyExtractor={(item, index) => `${item.id || ''}-${index}`}
              renderItem={({ item }) => renderMainItemRow(item)}
            />
          </View>
        ))}
        <View style={styles.notifierContainer}>
          {renderSectionDivider()}
          {renderOtherItems()}
        </View>
      </View>
    </Sidebar>
  );
};

export default DocketDetails;
